import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { DataGrid, GridColDef, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import RemoveShoppingCartOutlinedIcon from '@mui/icons-material/RemoveShoppingCartOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import TuneIcon from '@mui/icons-material/Tune';

import dayjs from 'dayjs';

import useAxios from '../../utils/useAxios';
import { SectionCard, StatCard, EmptyState, currency, number } from '../ui';
import { TRUE_COLORS } from '../../theme';

type Status = 'ok' | 'low' | 'out';
const statusOf = (qty: number, restock: number): Status => (qty <= 0 ? 'out' : restock > 0 && qty <= restock ? 'low' : 'ok');

export const StatusChip = ({ status }: { status: Status }) => {
  const map = {
    out: { label: 'Agotado', bg: 'rgba(192,57,43,0.1)', color: TRUE_COLORS.danger },
    low: { label: 'Stock bajo', bg: 'rgba(183,121,31,0.12)', color: TRUE_COLORS.warning },
    ok: { label: 'Disponible', bg: 'rgba(46,125,91,0.12)', color: TRUE_COLORS.success },
  }[status];
  return <Chip size="small" label={map.label} sx={{ bgcolor: map.bg, color: map.color, fontWeight: 600 }} />;
};

const REASONS = ['Recepción de mercancía', 'Conteo físico', 'Merma o daño', 'Devolución de cliente', 'Uso interno', 'Otro'];

/** Records an inventory adjustment (in, out, or a physical count) as an inventory log entry. */
const AdjustDialog = ({ item, onClose, onSaved }: { item: any | null; onClose: () => void; onSaved: () => void }) => {
  const axios = useAxios();
  const [mode, setMode] = React.useState<'in' | 'out' | 'count'>('in');
  const [qty, setQty] = React.useState('');
  const [reason, setReason] = React.useState(REASONS[0]);
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (item) { setMode('in'); setQty(''); setReason(REASONS[0]); setError(''); }
  }, [item]);
  React.useEffect(() => {
    setReason(mode === 'in' ? REASONS[0] : mode === 'count' ? REASONS[1] : REASONS[2]);
  }, [mode]);

  const current = Number(item?.quantity) || 0;
  const value = Number(qty);
  const change = qty === '' ? 0 : mode === 'in' ? value : mode === 'out' ? -value : value - current;

  const save = async () => {
    if (qty === '' || Number.isNaN(value) || value < 0) { setError('Ingresa una cantidad válida.'); return; }
    if (change === 0) { onClose(); return; }
    setSaving(true);
    try {
      await axios.post('inventory/inventory-log/', {
        product: item.product.id, location: item.location.id, change, reason, date: dayjs().format(),
      });
      onSaved();
      onClose();
    } catch {
      setError('No se pudo registrar el ajuste.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!item} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Ajustar inventario</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={{ fontWeight: 600 }}>{item?.product?.name}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {item?.location?.name} · {number(current, 2)} uds. actuales
            </Typography>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          <ToggleButtonGroup exclusive fullWidth size="small" value={mode} onChange={(_, v) => v && setMode(v)}>
            <ToggleButton value="in">Entrada</ToggleButton>
            <ToggleButton value="out">Salida</ToggleButton>
            <ToggleButton value="count">Conteo</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            autoFocus
            type="number"
            label={mode === 'count' ? 'Unidades contadas' : 'Cantidad'}
            value={qty}
            onChange={e => setQty(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') save(); }}
            slotProps={{ input: { endAdornment: <InputAdornment position="end">uds.</InputAdornment> }, htmlInput: { min: 0, step: 'any' } }}
          />
          <TextField select label="Motivo" value={reason} onChange={e => setReason(e.target.value)}>
            {REASONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>
          <Box sx={{ bgcolor: TRUE_COLORS.lightBg, borderRadius: 2, p: 1.5, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Quedarán</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
              {number(current + change, 2)} uds.
              {change !== 0 && (
                <Box component="span" sx={{ ml: 1, color: change > 0 ? TRUE_COLORS.success : TRUE_COLORS.danger }}>
                  ({change > 0 ? '+' : ''}{number(change, 2)})
                </Box>
              )}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="inherit" onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Registrar ajuste</Button>
      </DialogActions>
    </Dialog>
  );
};

const StockTab = ({ locations }: { locations: any[] }) => {
  const axios = useAxios();
  const navigate = useNavigate();
  const [items, setItems] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [location, setLocation] = React.useState('all');
  const [filter, setFilter] = React.useState<'all' | 'low' | 'out'>('all');
  const [adjusting, setAdjusting] = React.useState<any | null>(null);
  const [refresh, setRefresh] = React.useState(0);

  React.useEffect(() => {
    axios.get('inventory/inventory/')
      .then(r => setItems((r.data ?? []).filter((i: any) => i.product?.enabled !== false)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [axios, refresh]);

  const rows = React.useMemo(() => items.map(i => {
    const qty = Number(i.quantity) || 0;
    const restock = Number(i.restock_quantity) || 0;
    return { ...i, qty, restock, status: statusOf(qty, restock), value: qty * (Number(i.product?.cost) || 0) };
  }), [items]);

  const inLocation = rows.filter(r => location === 'all' || String(r.location?.id) === location);
  const visible = inLocation.filter(r => filter === 'all' || r.status === filter);
  const lowCount = inLocation.filter(r => r.status === 'low').length;
  const outCount = inLocation.filter(r => r.status === 'out').length;
  const totalValue = inLocation.reduce((acc, r) => acc + Math.max(r.value, 0), 0);

  const columns: GridColDef[] = [
    {
      field: 'product', headerName: 'Producto', flex: 1.6, minWidth: 220,
      valueGetter: (_v, row: any) => `${row.product?.name ?? ''} ${row.product?.barcode ?? ''}`,
      renderCell: ({ row }) => (
        <Stack sx={{ justifyContent: 'center', height: '100%', minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{row.product?.name}</Typography>
          <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{row.product?.barcode}</Typography>
        </Stack>
      ),
    },
    { field: 'location', headerName: 'Sucursal', flex: 0.9, minWidth: 140, valueGetter: (_v, row: any) => row.location?.name ?? '' },
    { field: 'qty', headerName: 'Existencias', type: 'number', flex: 0.6, minWidth: 110, valueFormatter: v => number(v, 2) },
    { field: 'restock', headerName: 'Reorden', type: 'number', flex: 0.5, minWidth: 100, valueFormatter: (v: number) => (v ? number(v, 2) : '—') },
    {
      field: 'status', headerName: 'Estado', flex: 0.6, minWidth: 120,
      sortComparator: (a: Status, b: Status) => ['out', 'low', 'ok'].indexOf(a) - ['out', 'low', 'ok'].indexOf(b),
      renderCell: ({ value }) => <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}><StatusChip status={value} /></Box>,
    },
    { field: 'value', headerName: 'Valor (costo)', type: 'number', flex: 0.6, minWidth: 120, valueFormatter: v => currency(v) },
    {
      field: 'actions', headerName: '', sortable: false, filterable: false, width: 110, align: 'right',
      renderCell: ({ row }) => (
        <Button size="small" startIcon={<TuneIcon fontSize="small" />} onClick={e => { e.stopPropagation(); setAdjusting(row); }}>
          Ajustar
        </Button>
      ),
    },
  ];

  return (
    <Stack spacing={3}>
      <Grid container spacing={3}>
        {[
          { label: 'Productos en inventario', value: number(new Set(inLocation.map(r => r.product?.id)).size), icon: <Inventory2OutlinedIcon /> },
          { label: 'Stock bajo', value: number(lowCount), icon: <WarningAmberOutlinedIcon />, onClick: () => setFilter('low') },
          { label: 'Agotados', value: number(outCount), icon: <RemoveShoppingCartOutlinedIcon />, onClick: () => setFilter('out') },
          { label: 'Valor del inventario', value: currency(totalValue), icon: <PaymentsOutlinedIcon /> },
        ].map(card => (
          <Grid key={card.label} size={{ xs: 6, sm: 6, lg: 3 }}>
            <StatCard label={card.label} value={card.value} icon={card.icon} loading={loading} onClick={card.onClick} />
          </Grid>
        ))}
      </Grid>

      <SectionCard noPadding>
        {!loading && rows.length === 0 ? (
          <EmptyState
            icon={<Inventory2OutlinedIcon />}
            title="Aún no hay inventario"
            description="Agrega productos y sucursales; las existencias aparecerán aquí."
            action={<Button variant="contained" onClick={() => navigate('/admin/add-product')}>Agregar producto</Button>}
          />
        ) : (
          <DataGrid
            rows={visible}
            columns={columns}
            loading={loading}
            autoHeight
            rowHeight={60}
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } }, sorting: { sortModel: [{ field: 'status', sort: 'asc' }] } }}
            onRowClick={({ row }) => navigate(`/admin/products/${row.product?.id}`)}
            slots={{
              toolbar: () => (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ px: 2, pt: 2, pb: 1, alignItems: { md: 'center' } }}>
                  <GridToolbarQuickFilter placeholder="Buscar producto o código…" debounceMs={200} sx={{ width: { xs: '100%', md: 300 } }} />
                  <Box sx={{ flexGrow: 1 }} />
                  <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}
                    sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.5 } }}>
                    <ToggleButton value="all">Todos</ToggleButton>
                    <ToggleButton value="low">Stock bajo ({lowCount})</ToggleButton>
                    <ToggleButton value="out">Agotados ({outCount})</ToggleButton>
                  </ToggleButtonGroup>
                  <TextField select size="small" label="Sucursal" value={location} onChange={e => setLocation(e.target.value)} sx={{ minWidth: 180 }}>
                    <MenuItem value="all">Todas</MenuItem>
                    {locations.map(l => <MenuItem key={l.id} value={String(l.id)}>{l.name}</MenuItem>)}
                  </TextField>
                </Stack>
              ),
              noRowsOverlay: () => <EmptyState compact title="Nada por aquí" description="No hay productos que coincidan con estos filtros." />,
            }}
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '--DataGrid-overlayHeight': '200px' }}
          />
        )}
      </SectionCard>

      <AdjustDialog item={adjusting} onClose={() => setAdjusting(null)} onSaved={() => setRefresh(r => r + 1)} />
    </Stack>
  );
};

export default StockTab;
